import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OrderDetails from './OrderDetails';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import { useFormik } from 'formik';
import { orderSchema, TAB_ERROR_PREFIXES } from './schemas/indexSchema';
import { client } from '../../../../util';
import type {
  Order,
  OrderCreateInput,
  OrderflowSettings,
  PaymentTerms,
  ShipMethods,
  TaxCode,
} from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import {
  closeModal,
  fetchOrderToEdit,
  OrderModalMode,
  setDisableButtons,
  setFormData,
  setLoading,
  setMode,
} from '../../../../state/orderModalSlice';
import DraggablePaper from '../../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { serverLatencyMessage } from '../../constants/messages';
import Items from './Items';
import dayjs from 'dayjs';
import {
  getTabsWithVisibleErrors,
  useFormikTabErrorMiddleware,
  tabErrorSx,
  tabIndicatorErrorSx,
  tabHasError,
} from '../../../utils/FormikTabErorrs';
import SlideUpTransition from '../../SlideUpTransition';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function CreateEditOrderModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tAddO } = useTranslation([], {
    keyPrefix: 'team.Modals.Sales Orders.createAndEdit.Add Sales Order',
  });

  const {
    mode,
    stateIsChanged,
    formData,
    isOpenOrderModal,
    orderIdToEdit,
    updateCallback,
    createCallback,
    loading,
    disableButtons,
  } = useSelector(({ orderModalSlice }) => orderModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const [tab, setTab] = useState(0);

  const dispatch = useDispatch();

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms[]>([]);
  const [shipMethods, setShipMethods] = useState<ShipMethods[]>([]);
  const [orderflowSettings, setOrderflowSettings] =
    useState<OrderflowSettings | null>(null);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);

  const errorTabTrigger = (tabsWithError: number[]) => {
    if (tabsWithError.length && tabsWithError[0] !== undefined)
      setTab(tabsWithError[0]);
  };

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const updateDisableButtons = (value: boolean) => {
    dispatch(setDisableButtons(value));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  const exitViewMode = async () => {
    dispatch(setMode(OrderModalMode.EDIT));
  };

  const getSuggestedCode = async () => {
    const code = await client.getSuggestedOrderCode.query();
    formik.setFieldValue('code', code);
  };
  const checkOrderCode = async (code: string) => {
    const number = Number(code);
    const resp = await client.getCounters.query();
    if (number < resp.orderNumber) {
      formik.setFieldError('code', `Must be more than ${resp.orderNumber - 1}`);
      return false;
    }
    return true;
  };

  const handleSave = async (payload: OrderCreateInput) => {
    updateLoading(true);
    if (mode == OrderModalMode.EDIT) {
      // check if modal is in edit mode
      if (orderIdToEdit) {
        // double check if id exists. Should never happen.
        try {
          const updatedOrder = await client.updateOrder.mutate({
            ...payload,
            rate: payload.rate / 100,
            OrderId: orderIdToEdit,
            orderDate: payload.orderDate ?? 0,
            estimatedShipDate: payload.estimatedShipDate ?? 0,
            deadline: payload.deadline ?? 0,
            items: payload.items.map((it: any) => {
              const { loading, primaryVendor, purchaseOrder, ...rest } = it;

              return {
                ...rest,
                // set VendorId (or null if vendor not set)
                primaryVendor: primaryVendor ? primaryVendor.VendorId : '',
                purchaseOrder: purchaseOrder
                  ? purchaseOrder.PurchaseOrderId
                  : '',
              };
            }),
          });
          if (updateCallback) updateCallback(updatedOrder);
          dispatch(
            pushToast({
              message: `Order ${updatedOrder?.code || ''} updated`,
              additionalInfo: serverLatencyMessage,
              type: 'success',
              duration: 7000,
            }),
          );
        } catch (error) {
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
          updateLoading(false);
          return;
        }
      } else {
        // this should never happaned but its good to have this
        console.log('ERROR: Order id is wrong or missing: ', orderIdToEdit);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } else {
      const isValid = await checkOrderCode(payload.code);
      if (!isValid) {
        updateLoading(false);
        setTab(0);
        return;
      }
      try {
        if (team === 'init' || team === 'loading') return;
        const newOrder: Order = await client.createOrder.mutate({
          ...payload,
          rate: payload.rate / 100,
          items: payload.items.map((it: any) => {
            const { loading, primaryVendor, purchaseOrder, ...rest } = it;

            return {
              ...rest,
              // set VendorId (or null if vendor not set)
              primaryVendor: primaryVendor ? primaryVendor.VendorId : '',
              purchaseOrder: purchaseOrder ? purchaseOrder.PurchaseOrderId : '',
            };
          }),
        });
        if (createCallback) createCallback(newOrder);
        dispatch(
          pushToast({
            message: `Order ${newOrder?.code || ''} created`,
            additionalInfo: serverLatencyMessage,
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
        updateLoading(false);
        return;
      }
    }
    handleClose();
    updateLoading(false);
  };

  // Use formData from redux as initialValues
  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: false, // Do not Reinitialize form values when formData in redux changes
    validationSchema: orderSchema,
    onSubmit: handleSave,
  });
  useFormikTabErrorMiddleware(formik, TAB_ERROR_PREFIXES, errorTabTrigger);

  // Sync formik values back to Redux when they change
  useEffect(() => {
    dispatch(setFormData(formik.values));
  }, [formik.values]);

  const fetchOptions = async () => {
    try {
      const [shipMethods, paymentTerms, settings, taxCodes] = await Promise.all(
        [
          client.allShippingMethods.query(),
          client.allPaymentTerms.query(),
          client.getSettings.query(),
          client.allTaxCodes.query(),
        ],
      );
      setShipMethods(shipMethods.results);
      setPaymentTerms(paymentTerms.results);
      setOrderflowSettings(settings);
      setTaxCodes(taxCodes.results);
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
  };

  const fetchDataForEdit = async () => {
    if (!orderIdToEdit) return;
    try {
      updateLoading(true);
      await fetchOptions();
      const order = await dispatch(fetchOrderToEdit(orderIdToEdit));

      // build items with vendor + purchaseOrder placeholders
      let items = formik.values.items;
      if (!items || items.length === 0) {
        // If formik items are empty, that means it first time opened and needed to fetch with api. No need to fetch when for example we just adding new item.
        items = (order.items || []).map((it: any) => {
          // ---- vendor placeholder ----
          const hasVendor = !!it.primaryVendor;
          const vendorWrapped = hasVendor
            ? {
                primaryVendor: {
                  VendorId: it.primaryVendor, // ID from backend
                },
                loadingV: true, // vendor is loadingV
              }
            : {
                primaryVendor: null,
                loadingV: false,
              };

          // ---- purchaseOrder placeholder ----
          const hasPO = !!it.purchaseOrder;
          const poWrapped = hasPO
            ? {
                purchaseOrder: {
                  PurchaseOrderId: it.purchaseOrder, // ID from backend
                },
                loadingPO: true,
              }
            : {
                purchaseOrder: null,
                loadingPO: false,
              };

          return {
            ...it,
            ...vendorWrapped,
            ...poWrapped,
          };
        });
      }

      // push order + items into Formik
      formik.setValues({
        ...formik.values, // keep any existing form values
        ...order, // backend order payload
        rate: (order.rate || 0) * 100,
        items, // items with placeholder vendor/PO
      });
      updateLoading(false);
      // Here we loaded main data so we can show to user but items are loading so we are disabling buttons
      updateDisableButtons(true);

      // ---------- VENDORS ----------
      const rowsNeedingVendor = items.filter(
        (row: any) =>
          row.loadingV === true &&
          row.primaryVendor &&
          row.primaryVendor.VendorId,
      );

      // nothing to resolve at all
      if (
        rowsNeedingVendor.length === 0 &&
        !items.some((r: any) => r.loadingPO)
      ) {
        return;
      }

      // single mutable copy of items, reused for vendor + PO updates
      let workingItems = [...items];

      const applyRowUpdate = (rowItemId: string, patch: any) => {
        workingItems = workingItems.map((existing: any) =>
          existing.itemId === rowItemId ? { ...existing, ...patch } : existing,
        );
        formik.setFieldValue('items', workingItems);
      };

      // fetch all vendors in parallel
      if (rowsNeedingVendor.length > 0) {
        await Promise.all(
          rowsNeedingVendor.map(async (row: any) => {
            try {
              const vendorData = await client.vendorById.query(
                row.primaryVendor.VendorId as string,
              );
              applyRowUpdate(row.itemId, {
                primaryVendor: vendorData,
                loadingV: false,
              });
            } catch (error: any) {
              if (error instanceof TRPCClientError) {
                dispatch(pushToast({ message: error.message, type: 'error' }));
              } else {
                dispatch(pushToast({ message: serverError, type: 'error' }));
              }
              applyRowUpdate(row.itemId, {
                primaryVendor: null,
                loadingV: false,
              });
            }
          }),
        );
      }

      // ---------- PURCHASE ORDERS ----------
      const rowsNeedingPO = workingItems.filter(
        (row: any) =>
          row.loadingPO === true &&
          row.purchaseOrder &&
          row.purchaseOrder.PurchaseOrderId,
      );

      if (rowsNeedingPO.length > 0) {
        await Promise.all(
          rowsNeedingPO.map(async (row: any) => {
            try {
              const poData = await client.purchaseOrderGetById.query(
                row.purchaseOrder.PurchaseOrderId as string,
              );
              applyRowUpdate(row.itemId, {
                purchaseOrder: poData,
                loadingPO: false,
              });
            } catch (error: any) {
              if (error instanceof TRPCClientError) {
                dispatch(pushToast({ message: error.message, type: 'error' }));
              } else {
                dispatch(pushToast({ message: serverError, type: 'error' }));
              }
              applyRowUpdate(row.itemId, {
                purchaseOrder: null,
                loadingPO: false,
              });
            }
          }),
        );
      }
    } catch (error) {
      handleClose();
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } finally {
      updateDisableButtons(false);
      updateLoading(false);
    }
  };

  useEffect(() => {
    // Here we don't make api calls if modal sate not changed (came back from other modal)
    // We also need to chek if its in edit mode because the state will be changed in it but we still need to fetch
    if (!isOpenOrderModal) return;
    if (stateIsChanged) return;
    const fetchData = async () => {
      updateLoading(true);
      await Promise.all([fetchOptions(), getSuggestedCode()]);
      if (orderIdToEdit) {
        await Promise.all([fetchDataForEdit()]);
      }
      updateLoading(false);
    };
    // Getting Initial data from formData (redux)
    formik.setValues(formData);
    fetchData();
  }, [isOpenOrderModal]);

  const renderTitle = () => {
    switch (mode) {
      case OrderModalMode.EDIT:
        return tAddO('Edit Sales Order');
      case OrderModalMode.VIEW:
        return tAddO('View Sales Order');
      case OrderModalMode.CREATE:
        return tAddO('Add Sales Order');
      default:
        return '';
    }
  };

  // This functions set default values
  useEffect(() => {
    if (orderIdToEdit) return; // If we are editing, do not set default values
    if (paymentTerms.length > 0) {
      const defaultTerm = paymentTerms.find((pT) => pT.default);
      if (defaultTerm) {
        formik.setFieldValue('paymentTerms', [defaultTerm]);
      }
    }
    if (shipMethods.length > 0) {
      const defaultShipMethods = shipMethods.find((sM) => sM.default);
      if (defaultShipMethods) {
        formik.setFieldValue('shipMethod', defaultShipMethods.ShipMethodId);
      }
    }
    if (orderflowSettings != null) {
      const orderD = formik.values.orderDate;
      const sD = orderflowSettings.purchaseOrders.shippingDays;
      const tD = orderflowSettings.purchaseOrders.trackingDays;
      if (orderD && sD && tD && sD > 0 && tD > 0) {
        formik.setFieldValue(
          'estimatedShipDate',
          dayjs.unix(orderD).add(sD, 'day').unix(),
        );
        formik.setFieldValue(
          'deadline',
          dayjs
            .unix(orderD)
            .add(sD + tD, 'day')
            .unix(),
        );
      }
    }
    if (taxCodes.length > 0) {
      const defaultTaxCode = taxCodes.find((tC) => tC.default);
      if (defaultTaxCode) {
        formik.setFieldValue('rate', defaultTaxCode.rate);
      }
    }
  }, [paymentTerms, shipMethods, orderflowSettings, taxCodes]); // Only run when `paymentTerms`, `shipMethods`, `orderflowSettings` or taxCodes changes

  // This just gets what tab has errors
  const tabsWithError = getTabsWithVisibleErrors(
    TAB_ERROR_PREFIXES,
    formik.errors,
    formik.touched,
  );

  return (
    <Dialog
      open={isOpenOrderModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      TransitionComponent={SlideUpTransition}
      maxWidth="xl"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {renderTitle()}
      </DialogTitle>
      <IconButton
        disabled={loading || disableButtons}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-around',
                  },
                }}
                TabIndicatorProps={{
                  sx: tabIndicatorErrorSx(tabHasError(tab, tabsWithError)),
                }}
              >
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(0, tabsWithError)),
                  ]}
                  label={tAddO('SALES ORDER DETAILS')}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(1, tabsWithError)),
                  ]}
                  label={tAddO('ITEMS')}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <OrderDetails
                formik={formik}
                paymentTerms={paymentTerms}
                shipMethods={shipMethods}
                taxCodes={taxCodes}
                viewMode={mode == OrderModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <Items formik={formik} viewMode={mode == OrderModalMode.VIEW} />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading || disableButtons}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {mode == OrderModalMode.VIEW ? (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                exitViewMode();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Edit')
              )}
            </Button>
          ) : (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Save')
              )}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
